import type { DrawerState, DrawerPosition } from '@app/shared/types/drawer'
import { RightDrawerContent } from '@app/shared/types/drawer'

import announceDrawerTransitions from './announce-transitions'

export default (doc: Document): Array<() => void> => {
  const unsubscribers: Array<() => void> = []

  const mainWrapper = doc.getElementById('main-wrapper')
  const rightDrawer = doc.getElementById('right-drawer')
  const leftDrawer = doc.getElementById('left-drawer')

  const announcers: Partial<Record<DrawerPosition, () => void>> = {}

  for (const [position, drawer] of [
    ['left', leftDrawer],
    ['right', rightDrawer],
  ] as Array<[DrawerPosition, HTMLElement | null]>) {
    if (!drawer) continue

    const announcer = announceDrawerTransitions(drawer)
    announcers[position] = announcer.announceMotion
    unsubscribers.push(announcer.unsubscribe)
  }

  // The width change is announced alongside the attribute that causes it, so
  // consumers hear the pair even when the drawer moves without a transition.
  // Only a state that actually changes announces: 'drawer:open' and
  // 'drawer:close' both fire for a drawer already in that state, and consumers
  // reposition on every pair they hear.
  const setDrawerState = (
    modifier: DrawerPosition,
    state: 'collapsed' | 'expanded'
  ): void => {
    window.requestAnimationFrame(() => {
      if (!mainWrapper) return
      if (mainWrapper.dataset[`${modifier}Drawer`] === state) return

      mainWrapper.dataset[`${modifier}Drawer`] = state
      announcers[modifier]?.()
    })
  }

  const collapseDrawer = (modifier: DrawerPosition): void => {
    setDrawerState(modifier, 'collapsed')
  }

  const expandDrawer = (modifier: DrawerPosition): void => {
    setDrawerState(modifier, 'expanded')
  }

  const handleDrawerOpen = (drawer: DrawerState): void => {
    if (drawer.direction !== 'left' && drawer.direction !== 'right') {
      return
    }

    window.requestAnimationFrame(() => {
      if (rightDrawer && drawer.direction === 'right') {
        if (
          [
            RightDrawerContent.FilePreview,
            RightDrawerContent.FileAttachments,
          ].includes(drawer.content)
        ) {
          rightDrawer.classList.add('sidebar-large')
        } else {
          rightDrawer.classList.remove('sidebar-large')
        }

        // The talking-points drawer (presentation mode) is full-height above the
        // top nav and pushes the page left; the styles key off
        // data-right-drawer-overlay on the wrapper (see two-drawer-layout-new.scss).
        if (mainWrapper) {
          if (drawer.content === RightDrawerContent.TalkingPoints) {
            mainWrapper.dataset.rightDrawerOverlay = 'true'
          } else {
            delete mainWrapper.dataset.rightDrawerOverlay
          }
        }
      }
    })

    expandDrawer(drawer.direction)
  }

  const handleDrawerClose = (direction?: DrawerPosition): void => {
    if (!direction) {
      return
    }

    collapseDrawer(direction)

    if (rightDrawer && direction === 'right') {
      window.requestAnimationFrame(() => {
        rightDrawer.classList.remove('sidebar-large')
        // Release the presentation-mode overlay so the drawer stops being
        // fixed/high-z-index once it's collapsed (see two-drawer-layout-new.scss).
        if (mainWrapper) {
          delete mainWrapper.dataset.rightDrawerOverlay
        }
      })
    }
  }

  window.eventHub.$on('drawer:open', handleDrawerOpen)
  window.eventHub.$on('drawer:close', handleDrawerClose)

  window.eventHub.$emit('drawer:ready')

  unsubscribers.push(() => {
    window.eventHub.$off('drawer:open', handleDrawerOpen)
    window.eventHub.$off('drawer:close', handleDrawerClose)
  })

  return unsubscribers
}
